import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useReducedMotion } from '../../animations';

interface ReadingSection {
    id: string;
    title: string;
    nested: boolean;
}

/** Read the rendered headings, so code fences and formatted titles stay honest. */
export function ArticleReaderNav({ sourceRef, articleKey, renderVersion }: {
    sourceRef: React.RefObject<HTMLDivElement>;
    articleKey: string;
    renderVersion: string | null;
}) {
    const location = useLocation();
    const reduced = useReducedMotion();
    const [sections, setSections] = useState<ReadingSection[]>([]);
    const [activeId, setActiveId] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [wide, setWide] = useState(() => window.matchMedia('(min-width: 1200px)').matches);

    useEffect(() => {
        setExpanded(false);
    }, [articleKey]);

    useEffect(() => {
        const media = window.matchMedia('(min-width: 1200px)');
        const update = () => setWide(media.matches);
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        const headings = Array.from(sourceRef.current?.querySelectorAll<HTMLElement>('h2[id], h3[id]') ?? []);
        setSections(headings.map(heading => ({
            id: heading.id,
            title: heading.textContent?.trim() || '',
            nested: heading.tagName === 'H3',
        })));

        const updateCurrent = () => {
            const current = [...headings].reverse().find(heading => heading.getBoundingClientRect().top <= 160);
            setActiveId(current?.id ?? headings[0]?.id ?? '');
        };
        updateCurrent();
        if (typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(updateCurrent, {
            rootMargin: '-80px 0px -65% 0px',
            threshold: 0,
        });
        headings.forEach(heading => observer.observe(heading));
        return () => observer.disconnect();
    }, [articleKey, renderVersion, sourceRef]);

    useEffect(() => {
        let id: string;
        try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        if (!sections.some(section => section.id === id)) return;
        // Run after route effects: the app also restores scroll on navigation.
        const frame = requestAnimationFrame(() => {
            const target = Array.from(sourceRef.current?.querySelectorAll<HTMLElement>('h2[id], h3[id]') ?? [])
                .find(heading => heading.id === id);
            if (!target) return;
            target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
            target.focus({ preventScroll: true });
            setActiveId(id);
        });
        return () => cancelAnimationFrame(frame);
    }, [location.hash, location.key, reduced, sections, sourceRef]);

    if (sections.length < 2) return null;

    return (
        <aside className="reader-outline">
            <details open={wide || expanded} onToggle={event => {
                if (!wide) setExpanded(event.currentTarget.open);
            }}>
                <summary>In this article <span>{sections.filter(section => !section.nested).length} sections</span></summary>
                <nav aria-label="Article sections">
                    <h2>In this article</h2>
                    <ol>
                        {sections.map(section => (
                            <li key={section.id} className={section.nested ? 'reader-outline__nested' : undefined}>
                                <Link to={{ pathname: location.pathname, search: location.search, hash: `#${section.id}` }}
                                    aria-current={activeId === section.id ? 'location' : undefined}>
                                    {section.title}
                                </Link>
                            </li>
                        ))}
                    </ol>
                </nav>
            </details>
        </aside>
    );
}
